import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useAuthContext } from '../../../context/auth.context'
import { getLmsInsightConnections } from '../../../api-new/state/analytics'
import { claimsKnown, lmsAdminVisible } from '../../lms-admin/lms-admin-gate'
import { useLmsAccess } from '../../lms-admin/lms-access'

// The source filter on the analytics reports: whether a session came straight
// from the app or through an LMS, and which LMS connection. One control, one
// choice, in this order: All sources, Direct, Any LMS, then each LMS
// connection of the organization by name (an archived one marked).
//
// The report routes take two query parameters (source=all|direct|lms, and
// lmsConnection=<id> with source=lms). All sources sends neither, so a report
// asked with no filter is the report as it was before there was one.
//
// The control exists only where the LMS admin does (lmsAdminVisible, staff
// only while the LMS is staff-only) AND the analytics service answered
// GET lms-insight/connections with 200 and at least one connection. Staff get
// 200 in every organization, so an organization with no LMS connection says so
// with an empty list: nothing to filter by, so no filter. Any other answer
// (404 where the organization has no LMS insights, 403, a failed request)
// leaves it out too, and a page with no control sends no parameters: it is the
// page it was.

const ALL = 'all'
const DIRECT = 'direct'
const ANY_LMS = 'lms'
const CONNECTION = 'lms:'

// The query a choice sends, without a leading "?": '' (all), 'source=direct',
// 'source=lms' or 'source=lms&lmsConnection=<id>'.
const queryOf = value => {
  if (value === DIRECT) return 'source=direct'
  if (value === ANY_LMS) return 'source=lms'
  if (typeof value === 'string' && value.startsWith(CONNECTION)) return `source=lms&lmsConnection=${encodeURIComponent(value.slice(CONNECTION.length))}`
  return ''
}

// A report path with the filter's query on it, whether or not the path
// already has a query of its own.
export const withSourceFilter = (path, query) => {
  if (!query) return path
  return `${path}${path.includes('?') ? '&' : '?'}${query}`
}

// What a General or Custom row shows in its Source column: Direct, or the
// name of the LMS connection it came through (LMS where the name is null).
export const sourceLabel = row => (row && row.source === 'lms' ? row.lmsConnection || 'LMS' : 'Direct')

export const SourceFilter = ({ value, connections, onChange }) =>
  <select className="form-select form-select-sm w-auto" aria-label="Source" value={value} onChange={e => onChange(e.target.value)}>
    <option value={ALL}>All sources</option>
    <option value={DIRECT}>Direct</option>
    <option value={ANY_LMS}>Any LMS</option>
    {connections.map(connection =>
      <option key={connection.id} value={`${CONNECTION}${connection.id}`}>
        {connection.archived ? `${connection.name} (archived)` : connection.name}
      </option>
    )}
  </select>

// The page's side of the filter: { settled, available, query, getQuery,
// control }. control is the select, or null where the filter is hidden; query
// is what the page puts on every request it makes to a report route
// (withSourceFilter), '' when there is nothing to send. getQuery is the same,
// read when called, for a function a grid keeps from its first render (its
// datasource). available says whether the filter exists, for a page that shows
// something only beside it (the Source column).
//
// settled says whether available is final, for a page that must not change its
// columns once its grid is up: a grid that keeps its columns' order (TableAG's
// maintainColumnOrder) puts a column that was not there before at the far
// right, and re-applies a definition's `hide` on every update, which undoes a
// person's own choice in the Columns panel. So a page mounts its grid only once
// settled, lists the Source column when available, and gives it no `hide`.
// Settled is the session's claims being known (the auth context starts with
// none until the shell's refresh answers, so nobody is staff yet) and, for
// staff, the connections having answered, whatever the answer. Anyone else is
// settled as soon as the claims are known: they never wait on the connections,
// which are never asked for.
export const useSourceFilter = () => {
  const { state } = useAuthContext()
  useLmsAccess()
  const staff = lmsAdminVisible(state)

  // undefined until the connections have answered (null: answered, none usable).
  const [connections, setConnections] = useState(undefined)
  const [value, setValue] = useState(ALL)

  useEffect(() => {
    if (!staff) {
      setConnections(undefined)
      return undefined
    }

    let live = true
    getLmsInsightConnections()
      .then(body => live && setConnections(body && Array.isArray(body.connections) ? body.connections : null))
      .catch(() => live && setConnections(null))
    return () => { live = false }
  }, [staff])

  const available = staff && Array.isArray(connections) && connections.length > 0
  const settled = claimsKnown(state) && (!staff || connections !== undefined)
  const query = available ? queryOf(value) : ''

  // Assigned while rendering, not in an effect: a grid's effect on the same
  // commit (reloadKey) runs before its page's and must already see it.
  const latest = useRef(query)
  latest.current = query
  const getQuery = useCallback(() => latest.current, [])

  const control = useMemo(
    () => (available ? <SourceFilter value={value} connections={connections} onChange={setValue} /> : null),
    [available, value, connections]
  )

  return useMemo(() => ({ settled, available, query, getQuery, control }), [settled, available, query, getQuery, control])
}
